import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import AddServiceDialog from './AddServiceDialog';
import EditServiceDialog from './EditServiceDialog';
import api from '../utils/api';
import './Services.css';

const Services = () => {
  const navigate = useNavigate();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedService, setSelectedService] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [draggedService, setDraggedService] = useState(null);
  const [dragOverService, setDragOverService] = useState(null);
  const [isReordering, setIsReordering] = useState(false);

  const sortServices = useCallback((items) => (
    [...items].sort((a, b) => {
      if (a.priority !== undefined && b.priority !== undefined) {
        return a.priority - b.priority;
      }
      return a.name.localeCompare(b.name);
    })
  ), []);

  const formatCreatedDate = useCallback((value) => {
    if (!value) {
      return null;
    }

    const parsedDate = new Date(value);
    if (Number.isNaN(parsedDate.getTime())) {
      return null;
    }

    return parsedDate.toLocaleDateString();
  }, []);

  const fetchServices = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get('/services');
      
      if (response.data.success) {
        setServices(sortServices(response.data.data));
      } else {
        throw new Error(response.data.message || 'Failed to fetch services');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to fetch services');
    } finally {
      setLoading(false);
    }
  }, [sortServices]);

  useEffect(() => {
    fetchServices();
  }, [fetchServices]);

  const handleAddService = async (serviceData) => {
    try {
      const response = await api.post('/services', serviceData);
      
      if (response.data.success) {
        setServices(prev => sortServices([...prev, response.data.data]));
        setShowAddDialog(false);
      } else {
        throw new Error(response.data.message || 'Failed to create service');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to create service');
    }
  };

  const handleEditService = async (id, serviceData) => {
    try {
      const response = await api.put(`/services/${id}`, serviceData);
      
      if (response.data.success) {
        setServices(prev => sortServices(prev.map(service => 
          service._id === id ? { ...service, ...response.data.data } : service
        )));
        setShowEditDialog(false);
        setSelectedService(null);
      } else {
        throw new Error(response.data.message || 'Failed to update service');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to update service');
    }
  };

  const handleDeleteService = async (id) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      const response = await api.delete(`/services/${id}`);
      
      if (response.data.success) {
        setServices(services.filter(service => service._id !== id));
      } else {
        throw new Error(response.data.message || 'Failed to delete service');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to delete service');
    }
  };

  const openEditDialog = (service) => {
    setSelectedService(service);
    setShowEditDialog(true);
  };

  const openSubunits = (service) => {
    navigate(`/services/${service._id}/subunits`, {
      state: { service: service }
    });
  };

  const filteredServices = services.filter(service =>
    service.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const canReorder = !searchTerm.trim() && filteredServices.length > 1;

  const handleDragStart = (e, service) => {
    if (searchTerm.trim()) {
      return;
    }

    setDraggedService(service);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, service) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (draggedService && draggedService._id !== service._id) {
      setDragOverService(service._id);
    }
  };

  const handleDrop = (e, targetService) => {
    if (searchTerm.trim()) {
      return;
    }

    e.preventDefault();

    if (!draggedService || draggedService._id === targetService._id) {
      setDraggedService(null);
      setDragOverService(null);
      return;
    }

    const draggedIndex = services.findIndex(service => service._id === draggedService._id);
    const targetIndex = services.findIndex(service => service._id === targetService._id);

    if (draggedIndex === -1 || targetIndex === -1) {
      return;
    }

    const newServices = [...services];
    const [removed] = newServices.splice(draggedIndex, 1);
    newServices.splice(targetIndex, 0, removed);

    const updatedServices = newServices.map((service, index) => ({
      ...service,
      priority: index + 1
    }));

    setServices(updatedServices);
    setDraggedService(null);
    setDragOverService(null);
    setIsReordering(true);
  };

  const handleDragEnd = () => {
    setDraggedService(null);
    setDragOverService(null);
  };

  const sendUpdatedPriorities = useCallback(async () => {
    if (!isReordering) {
      return;
    }

    try {
      const payload = services.map(service => ({
        _id: service._id,
        priority: service.priority
      }));

      const response = await api.put('/services/reorder', { services: payload });
      if (!response.data.success) {
        throw new Error(response.data.message || 'Failed to reorder services');
      }
    } catch (err) {
      setError(err.response?.data?.message || err.message || 'Failed to save new order');
      fetchServices();
    } finally {
      setIsReordering(false);
    }
  }, [fetchServices, isReordering, services]);

  useEffect(() => {
    if (isReordering) {
      const timeoutId = setTimeout(() => {
        sendUpdatedPriorities();
      }, 1000);

      return () => clearTimeout(timeoutId);
    }
  }, [isReordering, sendUpdatedPriorities]);

  if (loading) {
    return (
      <div className="services-container">
        <div className="loading-spinner">Loading services...</div>
      </div>
    );
  }

  return (
    <div className="services-container">
      <div className="services-header">
        <div className="header-left">
          <h2>Services Management</h2>
          <p className="subtitle">Manage all service units and their subunits</p>
          {canReorder && (
            <p className="reorder-hint">Drag and drop service cards to reorder them</p>
          )}
          {searchTerm.trim() && services.length > 1 && (
            <p className="search-hint">Clear search to reorder services</p>
          )}
        </div>
        <button 
          className="add-button"
          onClick={() => setShowAddDialog(true)}
        >
          + Add New Service
        </button>
      </div>

      {error && (
        <div className="error-message">
          {error}
          <button onClick={() => setError(null)} className="close-error">×</button>
        </div>
      )}

      <div className="search-container">
        <input
          type="text"
          placeholder="Search services by name..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="search-input"
        />
      </div>

      <div className="services-grid">
        {filteredServices.length === 0 ? (
          <div className="no-services">
            {searchTerm ? 'No services found matching your search.' : 'No services found.'}
          </div>
        ) : (
          filteredServices.map((service) => (
            <div
              key={service._id}
              className={`service-card ${draggedService?._id === service._id ? 'dragging' : ''} ${dragOverService === service._id ? 'drag-over' : ''}`}
              draggable={canReorder}
              onDragStart={(e) => handleDragStart(e, service)}
              onDragOver={(e) => handleDragOver(e, service)}
              onDrop={(e) => handleDrop(e, service)}
              onDragEnd={handleDragEnd}
            >
              {canReorder && (
                <div className="drag-handle" title="Drag to reorder">
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                    <circle cx="9" cy="5" r="1.5"/><circle cx="15" cy="5" r="1.5"/>
                    <circle cx="9" cy="12" r="1.5"/><circle cx="15" cy="12" r="1.5"/>
                    <circle cx="9" cy="19" r="1.5"/><circle cx="15" cy="19" r="1.5"/>
                  </svg>
                </div>
              )}
              <div className="service-header">
                <div className="service-info">
                  <h3 className="service-name">{service.name}</h3>
                  <div className="service-meta">
                    <span className="service-id">Ref {service._id.slice(-8)}</span>
                    {formatCreatedDate(service.createdAt) && (
                      <span className="service-date">Created {formatCreatedDate(service.createdAt)}</span>
                    )}
                  </div>
                </div>
                <div className="service-actions">
                  <button
                    onClick={() => openEditDialog(service)}
                    className="edit-button"
                    title="Edit"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => handleDeleteService(service._id)}
                    className="delete-button"
                    title="Delete"
                  >
                    🗑️
                  </button>
                </div>
              </div>
              <div className="service-footer">
                <button
                  onClick={() => openSubunits(service)}
                  className="view-button"
                  title="View Subunits"
                >
                  View Subunits
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {showAddDialog && (
        <AddServiceDialog
          onClose={() => setShowAddDialog(false)}
          onAdd={handleAddService}
        />
      )}

      {showEditDialog && selectedService && (
        <EditServiceDialog
          service={selectedService}
          onClose={() => {
            setShowEditDialog(false);
            setSelectedService(null);
          }}
          onEdit={handleEditService}
        />
      )}
    </div>
  );
};

export default Services;
